import { cn } from "@/lib/utils";
import { HTMLAttributes } from "react";

export function PageContainer({
  children,
  className,
  gradient,
}: {
  children: React.ReactNode;
  className?: string;
  gradient?: boolean;
}) {
  return (
    <div className={cn("relative", gradient && "overflow-hidden")}>
      {gradient && (
        <>
          <div className="from-primary/5 via-background to-background absolute inset-0 -z-10 bg-gradient-to-b" />
          <div className="bg-primary/15 absolute -top-16 left-1/2 -z-10 h-[250px] w-[250px] -translate-x-1/2 rounded-full blur-3xl md:h-[300px] md:w-[50%]" />
        </>
      )}
      <div className={cn("container mx-auto px-4 py-8", className)}>
        {children}
      </div>
    </div>
  );
}

export function LayoutPageHeader({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-8 flex items-start justify-between gap-4">
      {children}
    </div>
  );
}

export function LayoutPageHeaderActions({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return <div className={cn("flex gap-4", className)}>{children}</div>;
}

export function LayoutPageH1({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <h1 className={cn("text-3xl font-bold", className)}>{children}</h1>;
}

export function LayoutPageDescription({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p className={cn("text-muted-foreground mt-1", className)}>{children}</p>
  );
}

export function LayoutContent2Cols({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div className="mt-8 grid gap-8 md:grid-cols-[minmax(0,1fr)_320px]">
      {children}
    </div>
  );
}

export function LayoutContentMain({ children }: { children: React.ReactNode }) {
  return <main>{children}</main>;
}

export function LayoutContentAside({
  children,
}: {
  children: React.ReactNode;
}) {
  return <aside>{children}</aside>;
}

export function LayoutPageH2({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h2 {...props} className={cn("mb-2 text-xl font-semibold", className)}>
      {children}
    </h2>
  );
}

export function LayoutPageH2Description({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <p className={cn("text-muted-foreground text-sm", className)}>{children}</p>
  );
}

export function LayoutPageH3({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3 {...props} className={cn("mt-6 mb-2 text-lg font-semibold", className)}>
      {children}
    </h3>
  );
}
